import { useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { t } from '../../i18n.js'
import { filterTenants } from '../utils/tenantSearch.js'

// EVA-266 (Tier 1 unification): tenant-picker modal shared by OperatingExpensesTab
// and CapitalExpenditureTab (was byte-identical in both). It reads its strings from
// the parent's translations scope (`modal.*` + `unnamed`), so each grid keeps its
// own copy — e.g. the description differs (operating expense vs capital expenditure)
// — via i18n, not code. Pure search logic lives in utils/tenantSearch.js.
export default function TenantSelectorModal({ tenants, selectedIds, onSave, onClose, translations }) {
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState(() => new Set(selectedIds.map(Number)))

  const filtered = useMemo(() => filterTenants(tenants, query), [tenants, query])

  const toggle = (id) => {
    setPicked((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  return createPortal(
    <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t('modal.title', translations)}</h5>
            <button type="button" className="btn-close" aria-label={t('modal.close', translations)} onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <p className="text-muted small">{t('modal.description', translations)}</p>
            <input
              type="text" className="form-control mb-3" placeholder={t('modal.search_placeholder', translations)}
              value={query} onChange={(e) => setQuery(e.target.value)}
            />
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  <th style={{ width: '3rem' }}>{t('modal.col_select', translations)}</th>
                  <th>{t('modal.col_tenant_name', translations)}</th><th>{t('modal.col_suite', translations)}</th><th>{t('modal.col_lease_start', translations)}</th><th>{t('modal.col_lease_end', translations)}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((tenant) => (
                  <tr key={tenant.id} onClick={() => toggle(Number(tenant.id))} style={{ cursor: 'pointer' }}>
                    <td><input type="checkbox" className="form-check-input" checked={picked.has(Number(tenant.id))} readOnly /></td>
                    <td>{tenant.title || t('unnamed', translations)}</td>
                    <td>{tenant.suite}</td>
                    <td>{tenant.lease_start_date}</td>
                    <td>{tenant.lease_end_date}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="5" className="text-center text-muted">{t('modal.empty', translations)}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="modal-footer justify-content-between">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setPicked(new Set())}>
              {t('modal.clear', translations)}
            </button>
            <div>
              <button type="button" className="btn btn-light btn-sm me-2" onClick={onClose}>{t('modal.cancel', translations)}</button>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => onSave(Array.from(picked))}>
                {t('modal.save', translations)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
